import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Loader2, KeyRound, Eye, EyeOff } from 'lucide-react';
import { api, friendlyError } from '@/lib/api';
import { DASHBOARD } from '@/constants/testIds';
import { toast } from 'sonner';
import { SectionTitle } from '../SectionTitle';

/**
 * Sección "Cambiar contraseña" para el propio usuario autenticado.
 * Usa PATCH /api/users/me/password. No invalida la sesión actual.
 */
export function ChangePasswordSection() {
  const [form, setForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [show, setShow] = useState({ cur: false, nw: false, cf: false });
  const [saving, setSaving] = useState(false);

  const reset = () => setForm({ current_password: '', new_password: '', confirm_password: '' });

  const localValidate = () => {
    if (!form.current_password) return 'Introduce tu contraseña actual.';
    if (form.new_password.length < 8) return 'La nueva contraseña debe tener al menos 8 caracteres.';
    if (form.new_password !== form.confirm_password) return 'Las contraseñas no coinciden.';
    if (!/[A-Z]/.test(form.new_password)) return 'La contraseña debe incluir una mayúscula.';
    if (!/[a-z]/.test(form.new_password)) return 'La contraseña debe incluir una minúscula.';
    if (!/\d/.test(form.new_password)) return 'La contraseña debe incluir un número.';
    if (form.new_password === form.current_password) return 'La nueva contraseña debe ser distinta de la actual.';
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const err = localValidate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const res = await api.patch('/users/me/password', {
        current_password: form.current_password,
        new_password: form.new_password,
        confirm_password: form.confirm_password,
      });
      toast.success(res?.data?.message || 'Contraseña actualizada correctamente.');
      reset();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-5 sm:p-6 rounded-2xl bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]">
      <SectionTitle
        icon={KeyRound}
        label="Cambiar contraseña"
        hint="Tu sesión se mantendrá activa tras el cambio."
      />
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <PwdField
          id="pwd-current"
          label="Contraseña actual"
          value={form.current_password}
          onChange={(v) => setForm((f) => ({ ...f, current_password: v }))}
          show={show.cur}
          onToggle={() => setShow((s) => ({ ...s, cur: !s.cur }))}
          testId={DASHBOARD.profilePwdCurrent}
          autoComplete="current-password"
        />
        <PwdField
          id="pwd-new"
          label="Nueva contraseña"
          value={form.new_password}
          onChange={(v) => setForm((f) => ({ ...f, new_password: v }))}
          show={show.nw}
          onToggle={() => setShow((s) => ({ ...s, nw: !s.nw }))}
          testId={DASHBOARD.profilePwdNew}
          hint="Mínimo 8 caracteres con mayúsculas, minúsculas y un número."
          autoComplete="new-password"
        />
        <PwdField
          id="pwd-confirm"
          label="Confirmar nueva contraseña"
          value={form.confirm_password}
          onChange={(v) => setForm((f) => ({ ...f, confirm_password: v }))}
          show={show.cf}
          onToggle={() => setShow((s) => ({ ...s, cf: !s.cf }))}
          testId={DASHBOARD.profilePwdConfirm}
          autoComplete="new-password"
        />
        <Button
          type="submit"
          disabled={saving}
          data-testid={DASHBOARD.profilePwdSubmit}
          className="h-11 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white hover:shadow-[var(--ns-glow-gold)] border-0 transition-all"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Actualizar contraseña'}
        </Button>
      </form>
    </Card>
  );
}

function PwdField({ id, label, value, onChange, show, onToggle, testId, hint, autoComplete }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} className="text-sm font-medium">{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          data-testid={testId}
          className="h-11 bg-white pr-10"
          required
        />
        <button
          type="button"
          onClick={onToggle}
          aria-label={show ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--ns-text-muted)] hover:text-[var(--ns-text)] p-1"
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {hint ? <p className="text-[11px] text-[var(--ns-text-subtle)]">{hint}</p> : null}
    </div>
  );
}
